/* eslint-disable @typescript-eslint/no-explicit-any */
import type { AxiosInstance, AxiosRequestConfig } from "axios";
import axios from "axios";

// Very crude implementation of a local cache. Basic idea is that every time we make a get request,
// we check if our cache has what we're getting, and if it doesn't, make the get request and update
// the cache. We clear the cache on every POST, PATCH, DELETE, and PUT request just to be safe.
// TODO: have more precise way of only clearing the part of the cache that we need to on every non-get
// request.
const cache: Record<string, any | undefined> = {};
export const clearRequestsCache = () => {
  for (const key of Object.keys(cache)) {
    delete cache[key];
  }
};

export class Provider {
  private readonly axios: AxiosInstance;
  static token: string = "";
  static uid: string = "";
  static lassoToken: string = "";

  constructor(configInstance: AxiosRequestConfig<any>) {
    this.axios = axios.create({
      withCredentials: false,
      ...configInstance,
    });
  }

  static setToken(_token: string) {
    Provider.token = _token;
  }
  static setUid(uid: string) {
    Provider.uid = uid;
  }
  static setLassoToken(lassoToken: string) {
    Provider.lassoToken = lassoToken;
  }

  private static getHeader() {
    const token = localStorage.getItem("lassoToken") ?? Provider.token;

    return {
      Authorization: `Bearer ${token}`,
      uid: Provider.uid,
      "lasso-token": token,
      "Content-Type": "application/json",
    };
  }

  static setCookies(id: string, firebaseToken: string) {
    document.cookie = `idToken_uid=${id};max-age=3600;SameSite=Strict`;
    document.cookie = `idToken=${firebaseToken};max-age=3600;SameSite=Strict`;
  }

  protected get<T>(endpoint: string, config: AxiosRequestConfig = {}): Promise<T> {
    const cacheKey = `${endpoint}_${JSON.stringify(config)}`;
    if (cacheKey in cache && cache[cacheKey] !== undefined) {
      return cache[cacheKey];
    }

    const results = this.axios
      .get<T>(endpoint, {
        ...config,
        headers: {
          ...config.headers,
          ...Provider.getHeader(),
        },
      })
      .then((res) => res.data)
      .catch((err) => {
        return Promise.reject(this.handleError(err));
      });

    cache[cacheKey] = results;
    return results;
  }

  protected post<T>(endpoint: string, data: object = {}, config: AxiosRequestConfig = {}): Promise<T> {
    clearRequestsCache();

    return this.axios
      .post<T>(endpoint, data, {
        ...config,
        headers: {
          ...Provider.getHeader(),
          ...config.headers,
        },
      })
      .then((res) => res.data)
      .catch((err) => {
        return Promise.reject(this.handleError(err));
      });
  }

  protected update<T>(endpoint: string, data: object = {}, config: AxiosRequestConfig = {}): Promise<T> {
    clearRequestsCache();

    return this.axios
      .put<T>(endpoint, data, {
        ...config,
        headers: {
          ...config.headers,
          ...Provider.getHeader(),
        },
      })
      .then((res) => res.data)
      .catch((err) => {
        return Promise.reject(this.handleError(err));
      });
  }

  protected delete<T>(endpoint: string, config: AxiosRequestConfig = {}): Promise<T> {
    clearRequestsCache();

    return this.axios
      .delete<T>(endpoint, {
        ...config,
        headers: {
          ...config.headers,
          ...Provider.getHeader(),
        },
      })
      .then((res) => res.data)
      .catch((err) => {
        return Promise.reject(this.handleError(err));
      });
  }

  protected patch<T>(endpoint: string, data: object = {}, config: AxiosRequestConfig = {}): Promise<T> {
    clearRequestsCache();

    return this.axios
      .patch<T>(endpoint, data, {
        ...config,
        headers: {
          ...config.headers,
          ...Provider.getHeader(),
        },
      })
      .then((res) => res.data)
      .catch((err) => {
        return Promise.reject(this.handleError(err));
      });
  }

  private handleError(err: any): { error: { message: string; status: number; timestamp: number } } {
    return {
      error: {
        message: err?.message || "Unknown error",
        status: err?.response?.status ?? err?.status ?? 500,
        timestamp: new Date().getTime(),
      },
    };
  }
}
